/* Liquid Glass enhancer (CB-248). ES module, zero dependencies, starts after window load in idle time.
   - Tags JS-built lightbox controls (PhotoSwipe, Magnific) with the glass classes.
   - Sets data-lg-top / data-lg-bot on <html> from the section under the header and the bottom bars
     (computed section background + media coverage, no pixel reading).
   - Chromium only (html.lg-refract): edge refraction. A canvas displacement map (128 = no shift, R = x, B = y,
     neutral centre so only the rim bends inward) feeds an SVG feDisplacementMap used via backdrop-filter:url(#id),
     with a faint 3-pass per-channel fringe on small pills. At most 4 refracting elements; paused off-screen;
     maps are freed when a drawer closes or a section is unloaded in the theme editor.
     Technique after rizzytoday/liquid-glass (MIT, (c) 2026 Riz Roze); this is an independent implementation. */
const H = document.documentElement, D = document, NS = 'http://www.w3.org/2000/svg', SURF = H.dataset.lg || '';
const mq = (q) => matchMedia(q).matches;
const idle = (f) => (window.requestIdleCallback ? requestIdleCallback(f, { timeout: 2500 }) : setTimeout(f, 300));
const K = () => { const v = parseFloat(getComputedStyle(H).getPropertyValue('--lg-k')); return Number.isFinite(v) ? v : 0.5; };
let scan = () => {}, hdrOff = null; // hdrOff: tears down the current header controller (theme editor re-render)

if (H.hasAttribute('data-lg')) {
  if (/header/.test(SURF)) { // right away: it owns the header's scroll behaviour
    header();
    D.addEventListener('shopify:section:load', (e) => { if (e.target.classList.contains('helth-header')) header(); });
  }
  let go = 0;
  const once = () => { if (!go++) idle(start); };
  if (D.readyState === 'complete') once();
  else { addEventListener('load', once, { once: true }); setTimeout(once, 6000); } // a slow third-party file can hold load back 30 s+
}

function start() {
  tag();
  new MutationObserver((ms) => {
    for (const m of ms) for (const n of m.addedNodes) {
      if (n.nodeType === 1 && /pswp|mfp/.test(n.className)) {
        const o = new MutationObserver(tag);
        o.observe(n, { childList: true, subtree: true });
        setTimeout(() => o.disconnect(), 3000);
      }
    }
    tag();
  }).observe(D.body, { childList: true });
  if (/header|atc/.test(SURF)) tone();
  if (H.classList.contains('lg-refract') && !mq('(prefers-reduced-transparency: reduce)') && !mq('(prefers-contrast: more)')) refraction();
  if (mq('(pointer: fine) and (prefers-reduced-motion: no-preference)')) pointer();
  D.addEventListener('shopify:section:load', tag);
}

function tag() {
  if (/float/.test(SURF)) {
    D.querySelectorAll('.pswp__top-bar > .pswp__button:not(.lg)').forEach((b) => b.classList.add('lg', 'lg--pill', 'lg-r'));
    D.querySelectorAll('.mfp-container .mfp-arrow:not(.lg)').forEach((b) => b.classList.add('lg', 'lg--pill', 'lg--norim', 'lg-r'));
  }
  scan();
}

/* Header with hide-on-scroll (x-header[data-lg-scroll], replaces the theme's version): at the top it scrolls away
   with the page like a normal header (sticky one header height above the screen, so the browser moves it, no script).
   Once it is fully out of view it is pinned (.lg-pin: sticky top 0, glass) with its inner layer moved up out of
   sight; from then on scrolling up slides that layer in and scrolling down slides it out, so mid-page only a
   transform changes. Back at the very top, where both positions coincide, it is a normal header again.
   (iOS applies a change of the sticky top a frame late, so changing it while the bar is on screen made it drop twice.)
   The band, the glass and the bar all live on the inner layer and move together. */
function header() {
  if (hdrOff) hdrOff();
  const S = D.querySelector('.helth-header'), X = S && S.querySelector('[data-lg-scroll]'), M = X && X.parentElement;
  if (!M) return;
  const ac = new AbortController(), on = { passive: true, signal: ac.signal }, k = mq('(prefers-reduced-motion: reduce)') ? 0 : 1;
  const OFF = 'calc(-100% - 32px)'; // hidden: past the glass layer's soft shadow too
  let N = 0, h = 0, y0 = scrollY, acc = 0, raf = 0, pin = false, out = false, a = null, v, wd = 0, ih = innerHeight, sh = 0, rt = 0;
  const vis = (x) => { if (x !== v) H.style.setProperty('--header-is-visible', (v = x) ? '1' : '0'); };
  const size = () => { // natural top (N) and height (h) of the header section
    if (pin) return;
    S.style.position = 'static'; h = S.offsetHeight; N = 0; // static: no sticky or top offset
    for (let e = S; e; e = e.offsetParent) N += e.offsetTop;
    S.style.position = ''; S.style.setProperty('--lg-hh', h + 'px'); wd = innerWidth;
  };
  const busy = () => a && a.playState === 'running';
  const stop = (tf = '') => { if (a) a.cancel(); a = null; M.style.transform = tf; };
  const at = () => M.getBoundingClientRect().top - S.getBoundingClientRect().top; // where the inner layer is now
  const slide = (from, to, ms, done) => {
    stop('translateY(' + from + 'px)'); // inline start: the first frame is right even if the animation starts a frame late
    if (!k) { M.style.transform = to === '0px' ? '' : 'translateY(' + to + ')'; if (done) done(); return; }
    a = M.animate({ transform: ['translateY(' + from + 'px)', 'translateY(' + to + ')'] },
      { duration: ms, easing: to === '0px' ? 'cubic-bezier(.22,1,.36,1)' : 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' });
    if (done) a.onfinish = done;
  };
  const park = () => { // out of view: move the layer up, pin on the next frame (nothing on screen changes)
    if (!getComputedStyle(H).getPropertyValue('--lg-blur')) return; // liquid-glass.css not applied yet: keep scrolling like a normal header
    stop('translateY(' + OFF + ')'); out = true;
    requestAnimationFrame(() => { if (out && !pin) { S.classList.add('lg-pin'); pin = true; } });
  };
  const unpin = () => { stop(); S.classList.remove('lg-pin'); pin = out = false; size(); };
  const show = () => {
    if (!pin || !out) return;
    out = false; vis(1);
    slide(at(), '0px', 340, () => { stop(); if (scrollY <= N) unpin(); });
  };
  const hide = () => {
    if (!pin || out || X.querySelector('[open]')) return; // a mega menu or the search is open
    out = true; vis(0);
    slide(at(), OFF, 280);
  };
  const tick = () => {
    raf = 0;
    const y = Math.min(Math.max(0, scrollY), D.documentElement.scrollHeight - innerHeight), d = y - y0; // no rubber-band
    y0 = y;
    if (/(yhl-mm|so)-lock/.test(H.className)) return; // the drawer or the search has locked the page
    if (innerHeight !== ih) { ih = innerHeight; acc = 0; return; } // iOS toolbar collapsing / expanding: not the user's direction
    const H2 = D.documentElement.scrollHeight;
    if (H2 !== sh) { sh = H2; acc = 0; } // content above changed size: the browser moved the page, not the user
    else if (d) acc = (acc > 0) === (d > 0) ? acc + d : d;
    if (!pin) { // in flow: the browser scrolls it
      if (out && y < N + h) { stop(); out = false; } // came back before it was pinned
      else if (!out && y >= N + h) park();
      vis(y < N + h);
      return;
    }
    if (y <= N) { if (!busy()) out ? show() : unpin(); vis(1); return; } // at the top: a normal header again
    if (out && (acc < -10 || y < N + h)) show();
    else if (!out && acc > 24 && y >= N + h) hide();
  };
  // a click or keyboard focus inside a hidden or moving header brings it back and ends the slide first
  const settle = () => {
    if (!pin && (out || scrollY >= N + h)) { if (!out) stop('translateY(' + OFF + ')'); S.classList.add('lg-pin'); pin = out = true; }
    show(); if (busy()) a.finish();
  };
  size(); vis(scrollY < N + h);
  const ro = new ResizeObserver(() => size()); // logo / announcement heights settle after load
  for (let e = S; e; e = e.previousElementSibling) ro.observe(e);
  const mo = new MutationObserver(() => { if (/(yhl-mm|so)-lock/.test(H.className) && (out || busy())) settle(); });
  mo.observe(H, { attributes: true, attributeFilter: ['class'] });
  ac.signal.addEventListener('abort', () => { ro.disconnect(); mo.disconnect(); });
  hdrOff = () => { ac.abort(); stop(); S.classList.remove('lg-pin'); };
  addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(tick); }, on);
  addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => innerWidth !== wd && size(), 150); }, on);
  addEventListener('pointermove', (e) => {
    if (e.clientY >= 100 || e.pointerType !== 'mouse') return;
    if (pin) show(); else if (!out && scrollY >= N + h) park(); // parked now, shown on the next move
  }, on);
  S.addEventListener('click', settle, { capture: true, signal: ac.signal });
  S.addEventListener('focusin', () => { // focusing a control in the hidden bar made the browser scroll the page to it
    const back = y0; settle();
    if (pin && Math.abs(scrollY - back) > 2) scrollTo({ top: back, behavior: 'instant' });
  }, { signal: ac.signal });
}

/* Tone under the bars: "dark" when the section paints a dark background, "busy" when media covers much of it. */
function tone() {
  const memo = new WeakMap();
  const lum = (c) => {
    const v = c.match(/[\d.]+/g);
    if (!v || (v[3] !== undefined && +v[3] < 0.5)) return -1;
    const f = (x) => ((x /= 255) <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4);
    return 0.2126 * f(v[0]) + 0.7152 * f(v[1]) + 0.0722 * f(v[2]);
  };
  const skip = /^(STYLE|SCRIPT|LINK|TEMPLATE|NOSCRIPT|META)$/;
  const kids = (e) => [...e.children].filter((c) => !skip.test(c.tagName) && c.getBoundingClientRect().height > 0);
  const read = (s) => {
    if (memo.has(s)) return memo.get(s);
    const r = s.getBoundingClientRect(), A = r.width * r.height || 1;
    let L = -1, busy = false, area = 0;
    for (let e = s, i = 0; e && i < 6 && L < 0; i++) { // the section, then its first rendered child, and so on
      const cs = getComputedStyle(e);
      if (cs.backgroundImage !== 'none') busy = true;
      L = lum(cs.backgroundColor);
      if (L < 0) { const big = kids(e).find((c) => { const b = c.getBoundingClientRect(); return b.width * b.height > A * 0.5 && lum(getComputedStyle(c).backgroundColor) >= 0; }); if (big) L = lum(getComputedStyle(big).backgroundColor); }
      e = kids(e)[0];
    }
    if (!busy) for (const m of s.querySelectorAll('img,video')) {
      const b = m.getBoundingClientRect();
      if ((area += b.width * b.height) > A * 0.25) { busy = true; break; }
    }
    const v = (L >= 0 && L < 0.3 ? 'dark' : 'light') + (busy ? ' busy' : '');
    memo.set(s, v);
    return v;
  };
  // the band reports entering and leaving sections; the tone is the section that covers the band's line
  const band = (attr, rootMargin, at) => {
    const on = new Set();
    return new IntersectionObserver((es) => {
      for (const e of es) e.isIntersecting ? on.add(e.target) : on.delete(e.target);
      const y = innerHeight * at;
      for (const s of on) {
        const b = s.getBoundingClientRect();
        if (b.top <= y && b.bottom > y) { const v = read(s); if (H.getAttribute(attr) !== v) H.setAttribute(attr, v); break; }
      }
    }, { rootMargin });
  };
  const top = band('data-lg-top', '0px 0px -92% 0px', 0.04), bot = band('data-lg-bot', '-90% 0px 0px 0px', 0.95);
  const watch = () => D.querySelectorAll('.shopify-section').forEach((s) => {
    if (!/header/.test(s.className)) { top.observe(s); bot.observe(s); }
  });
  watch();
  D.addEventListener('shopify:section:load', watch);
  D.addEventListener('shopify:section:unload', (e) => { top.unobserve(e.target); bot.unobserve(e.target); });
}

function refraction() {
  const svg = D.createElementNS(NS, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';
  D.body.append(svg);
  const defs = new Map(), live = new Map(), seen = new Set(), known = new WeakSet();
  const M = ['1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0', '0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0', '0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0'];
  let uid = 0, timer = 0;
  const soon = () => { clearTimeout(timer); timer = setTimeout(plan, 400); }; // after drawer / sheet transitions settle
  // Edge-touching counts as "intersecting" (a closed drawer sits at x = 0), so require some visible area.
  const io = new IntersectionObserver((es) => {
    for (const e of es) {
      if (e.isIntersecting && e.intersectionRatio > 0.04) seen.add(e.target);
      else { seen.delete(e.target); off(e.target); }
      if (!e.target.isConnected) { io.unobserve(e.target); ro.unobserve(e.target); }
    }
    soon();
  }, { threshold: [0, 0.05] });
  const ro = new ResizeObserver(soon); // on() rebuilds only when the rounded size really changed
  scan = () => D.querySelectorAll('.lg-r').forEach((el) => {
    if (!known.has(el)) { known.add(el); io.observe(el); ro.observe(el); }
  });
  scan();
  new MutationObserver(soon).observe(H, { attributes: true, attributeFilter: ['class'] });
  // Theme editor: a section is about to be removed, so release the filters of the glass inside it (live is a subset of seen)
  D.addEventListener('shopify:section:unload', (e) => {
    for (const el of [...seen]) if (e.target.contains(el)) { seen.delete(el); io.unobserve(el); ro.unobserve(el); off(el); }
    soon();
  });

  function plan() {
    const ov = D.querySelector('.yhl-mm.is-open .yhl-mm__dialog, .ysb--sheet-open .ysb__sheet, .pswp--open, .mfp-wrap');
    const want = [...seen].filter((el) => el.isConnected && (!ov || ov.contains(el))).slice(0, 4);
    for (const el of [...live.keys()]) if (!want.includes(el)) off(el);
    want.forEach(on);
  }

  function on(el) {
    const w = el.offsetWidth, h = el.offsetHeight;
    if (!w || !h || w > 600 || h > 900) return;
    let cs = getComputedStyle(el);
    const rad = ['TopLeft', 'TopRight', 'BottomRight', 'BottomLeft'].map((c) => Math.min(parseFloat(cs['border' + c + 'Radius']) || 0, w / 2, h / 2));
    const key = [w, h, rad.join('/'), K()].join(':'), cur = live.get(el);
    if (cur && cur.key === key) return;
    if (cur) { off(el); cs = getComputedStyle(el); }
    if (cs.visibility === 'hidden') return;
    const base = cs.backdropFilter;
    if (!base || base === 'none') return;
    let d = defs.get(key);
    if (!d) defs.set(key, (d = build(w, h, rad)));
    d.n++;
    live.set(el, { key });
    d.ready.then(() => { if (live.get(el)?.key === key) el.style.backdropFilter = base + ' url(#' + d.id + ')'; });
  }

  function off(el) {
    const cur = live.get(el);
    if (!cur) return;
    live.delete(el);
    el.style.removeProperty('backdrop-filter');
    const d = defs.get(cur.key);
    if (d && --d.n <= 0) { d.node.remove(); defs.delete(cur.key); }
  }

  /* Displacement map: inward vector along the shape's normal, strength (1 - depth/rim)^2, zero past the rim.
     Max shift stays under rim/2 so the rim never folds. */
  function build(w, h, rad) {
    const id = 'lg-f' + ++uid, k = K(), q = Math.min(1, 220 / Math.max(w, h));
    const cw = Math.max(2, Math.round(w * q)), ch = Math.max(2, Math.round(h * q));
    const rim = Math.min(w, h) <= 120 ? Math.min(w, h) / 2 : 26, amp = rim * (0.18 + 0.27 * k);
    const c = D.createElement('canvas');
    c.width = cw; c.height = ch;
    const g = c.getContext('2d'), img = g.createImageData(cw, ch), a = img.data;
    for (let j = 0; j < ch; j++) for (let i = 0; i < cw; i++) {
      const x = (i + 0.5) / q, y = (j + 0.5) / q, L = x < w / 2, T = y < h / 2;
      const r = rad[T ? (L ? 0 : 1) : (L ? 3 : 2)], px = L ? x : w - x, py = T ? y : h - y;
      let dx = 0, dy = 0, depth;
      if (px < r && py < r) {
        const ux = r - px, uy = r - py, l = Math.hypot(ux, uy) || 1;
        depth = r - l; dx = ux / l; dy = uy / l;
      } else if (px < py) { depth = px; dx = 1; } else { depth = py; dy = 1; }
      const t = depth / rim, f = depth < 0 || t >= 1 ? 0 : (1 - t) * (1 - t), o = (j * cw + i) * 4;
      a[o] = 128 + 127 * (L ? dx : -dx) * f;
      a[o + 1] = 128;
      a[o + 2] = 128 + 127 * (T ? dy : -dy) * f;
      a[o + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    const url = c.toDataURL(), s = 2 * amp, node = D.createElementNS(NS, 'filter');
    node.id = id;
    [['x', 0], ['y', 0], ['width', 1], ['height', 1], ['color-interpolation-filters', 'sRGB']].forEach(([n, v]) => node.setAttribute(n, v));
    const dm = (sc, res) => '<feDisplacementMap in="SourceGraphic" in2="m" scale="' + sc.toFixed(1) + '" xChannelSelector="R" yChannelSelector="B"' + (res || '') + '/>';
    node.innerHTML = '<feImage href="' + url + '" preserveAspectRatio="none" result="m"/>' + (Math.max(w, h) <= 160
      ? M.map((m, n) => dm(s * (1 + n * 0.05)) + '<feColorMatrix values="' + m + '" result="c' + n + '"/>').join('') +
        '<feBlend in="c0" in2="c1" mode="screen" result="c3"/><feBlend in="c3" in2="c2" mode="screen"/>'
      : dm(s));
    svg.append(node);
    const im = new Image();
    im.src = url;
    return { id, node, n: 0, ready: im.decode().catch(() => 0) };
  }
}

/* Pointer-follow highlight on pills (fine pointers only). */
function pointer() {
  let raf = 0, last = null;
  D.addEventListener('pointermove', (e) => {
    const p = e.target.closest && e.target.closest('.lg--pill');
    if (!p) return;
    last = [p, e.clientX, e.clientY];
    if (!raf) raf = requestAnimationFrame(() => {
      raf = 0;
      const [el, x, y] = last, r = el.getBoundingClientRect();
      el.style.setProperty('--lg-angle', Math.round(Math.atan2(y - r.top - r.height / 2, x - r.left - r.width / 2) * 57.3 + 270) + 'deg');
    });
  }, { passive: true });
}
